import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import styles from './KpiFilterButton.module.css';

interface IKpiFilterButtonProps {
  /** The Materials search this control stands for — a quoted-name query
   *  (`namesQuery`), so both tiles land on exactly the same rows. */
  query: string;
  /** The live search, so the control reads as pressed while it is the one
   *  filtering the panels. */
  materialsQuery: string;
  onFilterMaterials: (query: string) => void;
  /** Tints the text; absent, it inherits its parent's colour. */
  severity?: 'critical' | 'warning';
  /** The query names orders by ref, not materials — Critical, Delivery late. */
  byOrder?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * A KPI tile's filter control: a piece of the tile's own text that, clicked,
 * filters the panels below to its materials. Clicking the one already driving
 * the filter clears it — a filter with no way back from the control that set
 * it is a trap. No chrome until you reach for it; the alerts tile's reasons
 * and the Delivered-not-synced label are both this.
 */
export function KpiFilterButton({ query, materialsQuery, onFilterMaterials, severity, byOrder = false, className, children }: IKpiFilterButtonProps) {
  const { t } = useTranslation();
  const active = materialsQuery === query;
  return (
    // ui/Tooltip's hint variant, not a native `title` — the styled replacement
    // frontend/CLAUDE.md §5 mandates for explanatory hover text on a control.
    <Tooltip content={t(byOrder ? 'logistics.kpi.orderFilterHint' : 'logistics.kpi.alertFilterHint')}>
      <button
        type="button"
        className={className ? `${styles.button} ${className}` : styles.button}
        data-severity={severity}
        aria-pressed={active}
        onClick={() => onFilterMaterials(active ? '' : query)}
      >
        {children}
      </button>
    </Tooltip>
  );
}
